import {
  FOC_TOUR_LABEL_AI_FOC,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_AI_FOC_TOUR_HREF as HREF } from '../focTourHrefs';
import {
  FOC_REQUIRED_DISPOSITION,
  focAiTourPopoverHtml,
  requirement,
  type FocAiTourStepDef,
} from './focAiTourTypes';

export const FOC_AI_FOC_TOUR_PARAM = 'focAiFocTour';
export const FOC_AI_FOC_TOUR_HREF = HREF;
export { focAiTourPopoverHtml };

export const FOC_AI_FOC_TOUR_STEPS: FocAiTourStepDef[] = [
  {
    id: 'sectionIntro',
    tour: 'pratus-ai-drawer',
    title: FOC_TOUR_LABEL_AI_FOC,
    description:
      'FOC AI requirements: permission-equivalent access, source transparency, authorized assistance with human review, prohibited autonomous actions, audit trail, approved corpus, and position-specific help.',
  },
  requirement({
    id: 'focAi001',
    requirementId: 'FOC-AI-001',
    name: 'Permission-Equivalent AI Access',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-drawer',
    description:
      'PRATUS AI accesses only information the requesting user is authorized to access, and only after the user explicitly selects or grants access to an authorized incident workspace, document, or other approved data source.',
  }),
  requirement({
    id: 'focAi002',
    requirementId: 'FOC-AI-002',
    name: 'AI Source Transparency',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-sources',
    description:
      'Provides source links, record references, source locations, timestamps, and an indication when it lacks sufficient authoritative information to support an output.',
  }),
  requirement({
    id: 'focAi003',
    requirementId: 'FOC-AI-003',
    name: 'Authorized AI Assistance',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-composer',
    description:
      'Supports authorized users with incident summarization, SITREP drafting, ICS form drafting, IAP narrative drafting, meeting-note drafting, checklist drafting, analysis of authorized documents, identification of incomplete products or dependencies, IMH/job-aid support, and source-grounded form prepopulation.',
    bullets: [
      'FOC-AI-003a — Human Review: users retain the ability to review, edit, reject, approve, and release AI-assisted content.',
      'FOC-AI-003b — AI Disclosure: PRATUS identifies when an output was created or materially assisted by AI.',
    ],
  }),
  requirement({
    id: 'focAi004',
    requirementId: 'FOC-AI-004',
    name: 'Prohibited Autonomous Actions',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-drawer',
    description:
      'PRATUS AI does not autonomously approve financial actions, obligate funds, initiate/approve procurement, authorize a resource, sign a document, publish/release an official product, make command decisions, modify authoritative records, bypass user permissions, or expose restricted information.',
  }),
  requirement({
    id: 'focAi005',
    requirementId: 'FOC-AI-005',
    name: 'AI Audit Trail',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-drawer',
    description:
      'Records AI workspace access, source selection, user prompts, output creation, material edits when technically available, approval/release action, and associated user/time information.',
  }),
  requirement({
    id: 'focAi006',
    requirementId: 'FOC-AI-006',
    name: 'Approved Knowledge Corpus and Source Authority',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-sources',
    description:
      'Can use approved Coast Guard doctrine, IMH content, approved ICS job aids, plans/forms/templates, authorized incident data, and other Government-approved sources. Prior IAPs, AARs, exercises, and lessons learned only when authorized and clearly distinguished from current doctrine.',
    bullets: [
      'FOC-AI-006a — Source Authority Label: results identify title/type, version or date when available, and authority/status to distinguish governing doctrine from local plans, historical records, exercises, and lessons learned.',
    ],
  }),
  requirement({
    id: 'focAi007',
    requirementId: 'FOC-AI-007',
    name: 'Position-Specific and Junior-User Assistance',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-composer',
    description:
      'Provides role-specific explanations, checklists, next-step guidance, examples, and doctrine references for authorized incident positions without changing the user’s authority or automatically completing command decisions.',
    bullets: [
      'FOC-AI-007a — Explain Before Recommend: explain the applicable process, identify required inputs/products/roles, cite the approved source, and distinguish doctrine from suggestions before optional drafting assistance.',
    ],
  }),
  requirement({
    id: 'focAi008',
    requirementId: 'FOC-AI-008',
    name: 'AAR and Exercise Learning Support',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'pratus-ai-drawer',
    description:
      'Authorized users can organize, compare, summarize, and identify recurring themes from approved AARs, exercise observations, and incident lessons learned. AI-generated themes do not become requirements, doctrine, policy, or approved corrective actions without human review and program approval.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_AI_FOC),
  },
];

export function focAiFocTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focAiFocTourStepIds(): string[] {
  return FOC_AI_FOC_TOUR_STEPS.map((step) => step.id);
}
